import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./talk-to-specialist.css";

const TalkToSpecialist = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    testInterest: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [formTouched, setFormTouched] = useState({
    fullName: false,
    email: false,
    phone: false,
    testInterest: false,
  });
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [notification, setNotification] = useState(null);

  // Medical tests state
  const [medicalTests, setMedicalTests] = useState([]);
  const [isLoadingTests, setIsLoadingTests] = useState(true);
  const [testsError, setTestsError] = useState(null);

  const CONTACT_API_ENDPOINT = "/wp-json/dbsb/v1/contact/talk-to-specialist";
  const MEDICAL_TESTS_API_ENDPOINT = "/wp-json/dbsb/v1/medical-tests";

  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
    disabled: { scale: 1 },
  };

  // Fetch medical tests on component mount
  useEffect(() => {
    const fetchMedicalTests = async () => {
      setIsLoadingTests(true);
      setTestsError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(`${MEDICAL_TESTS_API_ENDPOINT}?per_page=100`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "You must be logged in to view tests. Please log in and try again."
            );
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view tests.");
          } else {
            throw new Error("Failed to fetch tests. Please try again.");
          }
        }

        const data = await response.json();

        // Extract test titles from the API response
        const testTitles = data.tests.map((test) => ({
          id: test.id,
          title: test.title,
          slug: test.slug,
        }));

        setMedicalTests(testTitles);
      } catch (err) {
        console.error("Error fetching medical tests:", err);
        setTestsError(err.message);
        setMedicalTests([]);
      } finally {
        setIsLoadingTests(false);
      }
    };

    fetchMedicalTests();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Always validate if field has been touched or if we've attempted submit
    if (hasAttemptedSubmit || formTouched[name]) {
      setFormTouched((prev) => ({ ...prev, [name]: true }));
      // Validate with the new value immediately - always clear error if value is valid
      const newErrors = { ...formErrors };
      if (name === "fullName") {
        if (value.trim()) {
          delete newErrors.fullName;
        } else {
          newErrors.fullName = "Full name is required";
        }
      } else if (name === "email") {
        if (value.trim()) {
          if (/\S+@\S+\.\S+/.test(value)) {
            delete newErrors.email;
          } else {
            newErrors.email = "Please enter a valid email address";
          }
        } else {
          newErrors.email = "Email is required";
        }
      } else if (name === "phone") {
        if (value.trim()) {
          const phoneNum = value.replace(/\D/g, "");
          if (phoneNum.length >= 10 && phoneNum.length <= 15) {
            delete newErrors.phone;
          } else {
            newErrors.phone = "Please enter a valid phone number";
          }
        } else {
          newErrors.phone = "Phone number is required";
        }
      } else if (name === "testInterest") {
        if (value.trim()) {
          delete newErrors.testInterest;
        } else {
          newErrors.testInterest = "Test interest is required";
        }
      }
      setFormErrors(newErrors);
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setFormTouched((prev) => {
      const updated = { ...prev, [name]: true };
      // Validate immediately with updated touched state
      const newErrors = { ...formErrors };
      if (name === "fullName") {
        if (formData.fullName && formData.fullName.trim()) {
          delete newErrors.fullName;
        } else {
          newErrors.fullName = "Full name is required";
        }
      } else if (name === "email") {
        if (formData.email && formData.email.trim()) {
          if (/\S+@\S+\.\S+/.test(formData.email)) {
            delete newErrors.email;
          } else {
            newErrors.email = "Please enter a valid email address";
          }
        } else {
          newErrors.email = "Email is required";
        }
      } else if (name === "phone") {
        if (formData.phone && formData.phone.trim()) {
          const phoneNum = formData.phone.replace(/\D/g, "");
          if (phoneNum.length >= 10 && phoneNum.length <= 15) {
            delete newErrors.phone;
          } else {
            newErrors.phone = "Please enter a valid phone number";
          }
        } else {
          newErrors.phone = "Phone number is required";
        }
      } else if (name === "testInterest") {
        if (formData.testInterest && formData.testInterest.trim()) {
          delete newErrors.testInterest;
        } else {
          newErrors.testInterest = "Test interest is required";
        }
      }
      setFormErrors(newErrors);
      return updated;
    });
  };

  // Validate form
  const validateForm = (shouldValidateAll = false) => {
    const newErrors = {};
    const touched = formTouched;

    if ((shouldValidateAll || touched.fullName) && !formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    if (shouldValidateAll || touched.email) {
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }
    if (shouldValidateAll || touched.phone) {
      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else {
        const phoneNum = formData.phone.replace(/\D/g, "");
        if (phoneNum.length < 10 || phoneNum.length > 15) {
          newErrors.phone = "Please enter a valid phone number";
        }
      }
    }
    if ((shouldValidateAll || touched.testInterest) && !formData.testInterest.trim()) {
      newErrors.testInterest = "Test interest is required";
    }

    setFormErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  // Check if form is invalid
  const isFormInvalid = () => {
    // Check if there are any errors
    const hasErrors = Object.keys(formErrors).length > 0 && 
      Object.values(formErrors).some((error) => error !== "" && error !== undefined);
    
    // Check if required fields are incomplete
    const missingRequiredFields = 
      !formData.fullName.trim() || 
      !formData.email.trim() || 
      !formData.phone.trim() || 
      !formData.testInterest.trim();
    
    return hasErrors || missingRequiredFields;
  };

  // Show notification
  const showNotification = (message, type = "error") => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    // Mark all fields as touched
    setFormTouched({
      fullName: true,
      email: true,
      phone: true,
      testInterest: true,
    });

    // Validate form
    if (!validateForm(true)) {
      showNotification("Please fill in all required fields correctly");
      return;
    }

    setIsSubmitting(true);

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }

      const response = await fetch(CONTACT_API_ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        body: JSON.stringify({
          full_name: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          test_interest: formData.testInterest.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit your request. Please try again.");
      }

      // Success - show success state
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      showNotification(error.message || "Failed to submit your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="react-app-wrapper">
      <div className="talk-to-specialist-container">
        <motion.div
          className="talk-to-specialist-content"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <h2>Talk to a Specialist</h2>
          <motion.div className="talk-to-specialist-card" variants={itemVariants}>
            <div className="talk-to-specialist-wrapper">
              {/* Left Section */}
              <div className="left-section">
                <h3 className="specialist-title">Talk to a Specialist</h3>
                <p className="specialist-description">
                  Not sure where to start? We're happy to help. <br />
                  <span className="description-second-line">Share your contact details and a specialist will call you to answer your questions and guide you through your test options.</span>
                </p>
              </div>

              {/* Right Section */}
              <div className="right-section">
                {!isSubmitted ? (
                  <>
                    {/* Form Container */}
                    <motion.div
                      className="form-container"
                      variants={itemVariants}
                    >
                      <form className="specialist-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                          <label>Full Name</label>
                          <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter full name"
                          />
                          {formErrors.fullName && (
                            <span className="error">{formErrors.fullName}</span>
                          )}
                        </div>

                        <div className="form-group" style={{ marginTop: "24px" }}>
                          <label>Email Address</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter email address"
                          />
                          {formErrors.email && (
                            <span className="error">{formErrors.email}</span>
                          )}
                        </div>

                        <div className="form-group" style={{ marginTop: "24px" }}>
                          <label>Phone Number</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter phone number"
                          />
                          {formErrors.phone && (
                            <span className="error">{formErrors.phone}</span>
                          )}
                        </div>

                        <div className="form-group" style={{ marginTop: "24px" }}>
                          <label>Test Interest</label>
                          <select
                            name="testInterest"
                            value={formData.testInterest}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoadingTests}
                          >
                            <option value="">
                              {isLoadingTests ? "Loading tests..." : "Select from dropdown"}
                            </option>
                            {medicalTests.map((test) => (
                              <option key={test.id} value={test.title}>
                                {test.title}
                              </option>
                            ))}
                          </select>
                          {testsError && (
                            <span className="error" style={{ marginTop: "0.5rem", display: "block" }}>
                              {testsError}
                            </span>
                          )}
                          {formErrors.testInterest && (
                            <span className="error">{formErrors.testInterest}</span>
                          )}
                        </div>
                      </form>
                    </motion.div>

                    {/* Submit Button */}
                    <motion.div
                      className="button-group"
                      variants={itemVariants}
                      style={{ marginTop: "3rem" }}
                    >
                      <motion.button
                        type="submit"
                        onClick={handleSubmit}
                        className={`submit-button ${
                          isSubmitting || isFormInvalid() ? "disabled" : ""
                        }`}
                        disabled={isSubmitting || isFormInvalid()}
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap={
                          !isSubmitting && !isFormInvalid() ? "tap" : "disabled"
                        }
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </motion.button>
                    </motion.div>
                  </>
                ) : (
                  /* Success State */
                  <motion.div
                    className="success-container"
                    variants={itemVariants}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h3 className="success-title">Request Sent Successfully!</h3>
                    <p className="success-description">
                      Thank you! A specialist will contact you soon to discuss your test and answer any questions you may have. <br />
                      <span className="description-second-line">We usually respond within 1–2 business hours during working days.</span>
                    </p>
                    <motion.button
                      className="browse-tests-button"
                      onClick={() => navigate("/order-a-test")}
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                      style={{ marginTop: "24px" }}
                    >
                      Browse tests
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Notification Popup */}
        <AnimatePresence>
          {notification && (
            <motion.div
              className="notification-popup"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TalkToSpecialist;
